/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import React, { Fragment } from 'react'
import { FormattedMessage } from 'react-intl'

import { EuiCallOut, EuiSpacer } from '@elastic/eui'

import type { ElasticsearchClusterPlan, InstanceConfiguration } from '@modules/cloud-api/v1/types'

import DocLink from '../../DocLink'
import { hasDisabledDataTier } from '../../../lib/stackDeployments/dataTiers'

import type { FunctionComponent } from 'react'
import type { AutoscalingEnabled } from '@/lib/stackDeployments/autoscaling'

export type Props = {
  current?: ElasticsearchClusterPlan | null
  next?: ElasticsearchClusterPlan | null
  instanceConfigurations: InstanceConfiguration[]
  pruneOrphans?: boolean
  autoscalingStatus: AutoscalingEnabled
}

export const DisabledDataTierWarning: FunctionComponent<Props> = ({
  instanceConfigurations,
  current,
  next,
  pruneOrphans,
  autoscalingStatus,
}) => {
  const disabledDataTierInDiff = hasDisabledDataTier({
    current,
    next,
    pruneOrphans: pruneOrphans || false,
    instanceConfigurations,
    autoscalingStatus,
  })

  if (!disabledDataTierInDiff) {
    return null
  }

  return (
    <Fragment>
      <EuiCallOut color='warning' data-test-id='disable-data-tier-warning'>
        <FormattedMessage
          id='explain-changes.disable-instance-configuration.reallocation-warning'
          defaultMessage='Disabling a data tier may require manual shard reallocation. {readMore}'
          values={{
            readMore: (
              <DocLink link='removingADataTier'>
                <FormattedMessage
                  id='explain-changes.disable-instance-configuration.with-warning.learn-more'
                  defaultMessage='Learn more'
                />
              </DocLink>
            ),
          }}
        />
      </EuiCallOut>
      <EuiSpacer size='m' />
    </Fragment>
  )
}
